import { NgClass, NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Inject, Input, LOCALE_ID, Output, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AddToCartComponent, ProductPriceComponent, SelectYourOptionsComponent } from '@scalefast/components/product';
import { LocalizationConfigService, StoreConfigService } from '@scalefast/config-angular';
import { CartProduct, Metaproduct, Product, ProductPrice, ProductStock, createCartProductFromProduct } from '@scalefast/core';
import { AddToCartService, CartService, CrossSellProductsService } from '@scalefast/ecommerce-core';
import { AppCrossSellProductsTemplateComponent } from '../../../components/product/cross-sell-products/cross-sell-products-template/cross-sell-products-template.component';
import { AppModalComponent } from '../../../shared/components/modal/modal.component';
import { AppProductAddedToCartComponent } from '../product-added-to-cart/product-added-to-cart.component';
import { AppProductCardComponent } from '../product-card/product-card.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, map, switchMap, take } from 'rxjs';

@Component({
  selector: 'app-product-list',
  styleUrls: ['./product-list.component.scss'],
  templateUrl: './product-list.component.html',
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    NgOptimizedImage,
    ProductPriceComponent,
    AddToCartComponent,
    SelectYourOptionsComponent,
    AppModalComponent,
    AppProductAddedToCartComponent,
    AppCrossSellProductsTemplateComponent,
    AppProductCardComponent,
  ],
})
export class AppStoreProductListComponent {
  @ViewChild(AppModalComponent) modalComponent: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalErrorComponent') modalErrorComponent: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalErrorComponentCartMaxValue') modalErrorComponentCartMaxValue: AppModalComponent = new AppModalComponent();

  @Input() products: Product[] | undefined = undefined;
  @Input() metaproducts: Metaproduct[] | undefined = [];
  @Input() productRequested: Product | undefined;
  @Input() colorReferences: Record<string, string> | undefined = {};
  @Input() isInWishlist: boolean;

  @Input() isFilterOpen: boolean = false;
  @Input() displaySpecificProductCampaignHighlights: boolean = false;

  emptyList = Array(8).fill(0);

  /**
   * We have to specify the height and width of the images to avoid layout shifts
   * the width & height will only affect the space the DOM will reserve for the image
   * once the image is loaded it will take the space it needs
   * If size is not defined it will assume it will take 100vw and it will probably download a much bigger image than needed
   * For more info go to --> https://angular.io/guide/image-directive
   */
  @Input() imgHeight = '277';
  @Input() imgWidth = '277';
  @Input() size = '25vw';
  @Input() showSkeleton = false;
  @Output() addToWishlistEvent = new EventEmitter<MouseEvent>();
  @Output() removeFromWishlistEvent = new EventEmitter<MouseEvent>();
  @Output() checkLoggedUserEvent = new EventEmitter();
  @Output() setWishlistedProductEvent = new EventEmitter<Product>();
  @Output() openSuccessfullyAddedModalEvent = new EventEmitter<unknown>();
  @Output() getProductById = new EventEmitter<string>();
  @Output() addCrossSellProductToCartEventHandler = new EventEmitter<Product>();
  @Output() getMetaproductsEventHandler = new EventEmitter<string>();

  cartProductAdded: CartProduct | undefined;
  cartMetaproduct: CartProduct | undefined;
  modalTitle = '';
  showAddedToCartModal = true;
  productPrice: ProductPrice | undefined;
  productStock: ProductStock | undefined;
  uniqueModalId: string | undefined;
  crossSellProducts: Product[] = [];

  MAX_ITEM_QUANTITY: number;
  localeId: string;
  private storeConfig;
  readonly currency;

  protected addToCartService = inject(AddToCartService);
  protected crossSellProductsService = inject(CrossSellProductsService);
  protected destroyRef = inject(DestroyRef);
  protected showQuantityErrorModal: boolean = false;

  constructor(
    private storeConfigService: StoreConfigService,
    private localizationConfigService: LocalizationConfigService,
    protected cartService: CartService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.isInWishlist = false;
    this.uniqueModalId = this.generateUniqueId();
    this.storeConfig = this.storeConfigService.get();
    this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;
    this.localeId = this.locale;
    this.currency = this.localizationConfigService.get().currency;
  }

  generateUniqueId(): string {
    return Math.random().toString();
  }

  getProductPrice(cartProduct: CartProduct | undefined): ProductPrice | undefined {
    return this.products && this.products.find((product) => product.id === cartProduct?.productId)?.price;
  }

  getProductStock(cartProduct: CartProduct | undefined): ProductStock | undefined {
    return this.products && this.products.find((product) => product.id === cartProduct?.productId)?.stock;
  }

  getProductQuantity(cartProduct: CartProduct | undefined): number {
    return cartProduct ? cartProduct.quantity : 1;
  }

  transformProductToCartProduct(product: Product): CartProduct {
    return createCartProductFromProduct(product);
  }

  onAddToCart(product: Product): void {
    this.setWishlistedProductEvent.emit(product);
    this.modalTitle = 'Product successfully added to you cart!';

    const cartProduct: CartProduct = createCartProductFromProduct(product);

    // Set the cartProduct to the selected one
    this.cartProductAdded = cartProduct;

    // Get the cross-sell-products
    this.getCrossSellProducts(product);

    // We need to set also the product price and stock info
    this.productPrice = product.price;
    this.productStock = product.stock;

    this.uniqueModalId = this.generateUniqueId();

    // Control product list modals show
    this.showQuantityErrorModal = false;

    this.addToCartService
      .maxOrderValueReached(cartProduct.quantity)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        switchMap((status) => {
          if (status) {
            this.showQuantityErrorModal = true;
            this.modalErrorComponentCartMaxValue.show();
            return EMPTY;
          }
          return this.addToCartService.getProductQuantity(cartProduct.productId).pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef),
            switchMap((productQuantity) => {
              if (productQuantity + cartProduct.quantity > this.MAX_ITEM_QUANTITY) {
                this.showQuantityErrorModal = true;
                this.modalErrorComponent.show();
                return EMPTY;
              }
              return this.addToCartService.onAddToCart(cartProduct);
            }),
          );
        }),
      )
      .subscribe(() => {
        if (!this.showQuantityErrorModal) {
          this.modalComponent.show();
        }
      });
  }

  onGetProductEvent(productId: string): void {
    this.getProductById.emit(productId);
  }

  instanceOfProduct(product: Product | CartProduct): product is Product {
    return 'metaproductsV4' in product;
  }

  closeAddedToCartModal(): void {
    this.modalComponent.close();
  }

  addToWishlistEventHandler(event: MouseEvent): void {
    this.addToWishlistEvent.emit(event);
  }

  removeFromWishlistEventHandler(event: MouseEvent): void {
    this.removeFromWishlistEvent.emit(event);
  }

  checkLoggedUserEventHandler(): void {
    this.checkLoggedUserEvent.emit();
  }

  getCrossSellProducts(product: Product): void {
    this.crossSellProductsService
      .getCrossSellProducts(product)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // Filter out metaproducts from related products array
        map((crossSellProducts) => crossSellProducts.filter((product) => product.type !== 'META')),
      )
      .subscribe((filteredCrossSellProducts) => {
        this.crossSellProducts = filteredCrossSellProducts;
      });
  }

  closeQtyErrorModal(): void {
    this.modalErrorComponent.close();
  }

  closeQuantityErrorCartMaxValueModal(): void {
    this.modalErrorComponentCartMaxValue.close();
  }
}
